import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle, XCircle, Users, Hammer } from "lucide-react";
import { toast } from "sonner";

export default function AdminObras() {
  const [obras, setObras] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [obs, setObs] = useState({});

  const load = async () => {
    const [o, p] = await Promise.all([api.get("/obras/admin"), api.get("/obras/admin/prestadores")]);
    setObras(o.data);
    setPrestadores(p.data);
  };
  useEffect(() => { load(); }, []);

  const decidir = async (id, status) => {
    await api.post(`/obras/admin/${id}/aprovar`, { status, observacoes_admin: obs[id] || "" });
    toast.success(`Obra ${status === "APROVADA" ? "aprovada" : "rejeitada"}`);
    load();
  };

  const statusBadge = (s) => {
    const map = {
      AGUARDANDO_APROVACAO: { c: "border-warning/40 text-warning bg-warning/5", l: "Aguardando" },
      APROVADA: { c: "border-success/40 text-success bg-success/5", l: "Aprovada" },
      REJEITADA: { c: "border-destructive/40 text-destructive bg-destructive/5", l: "Rejeitada" },
      EM_ANDAMENTO: { c: "border-info/40 text-info bg-info/5", l: "Em andamento" },
      CONCLUIDA: { c: "border-foreground/40 text-foreground", l: "Concluída" },
    };
    const m = map[s] || { c: "border-border", l: s };
    return <Badge variant="outline" className={`${m.c} uppercase text-[10px] tracking-widest`}>{m.l}</Badge>;
  };

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="label-tag mb-2">Aprovação de reformas · Controle de acesso</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Obras & Prestadores</h1>
      </div>

      <Tabs defaultValue="obras">
        <TabsList className="rounded-sm">
          <TabsTrigger data-testid="tab-obras" value="obras"><Hammer size={12} className="mr-2" />Obras solicitadas ({obras.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-prestadores" value="prestadores"><Users size={12} className="mr-2" />Prestadores autorizados ({prestadores.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="obras" className="mt-6 space-y-3">
          {obras.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-sm">
              Nenhuma solicitação de obra.
            </div>
          ) : (
            obras.map((o) => (
              <Card key={o.id} data-testid={`obra-${o.id}`} className="p-5 rounded-sm border-border">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-heading font-bold text-lg">{o.titulo}</span>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{o.tipo}</Badge>
                      {statusBadge(o.status)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Solicitante: <b>{o.razao_social}</b> · Cronograma: {fmtDate(o.data_inicio)} → {fmtDate(o.data_fim)}
                    </div>
                    <p className="text-sm mt-3 leading-relaxed">{o.descricao}</p>
                    {o.anexos?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-2 font-mono">
                        {o.anexos.length} anexo(s) enviado(s)
                      </div>
                    )}
                    {o.observacoes_admin && (
                      <div className="text-xs mt-2 border-l-2 border-border pl-3 text-muted-foreground">
                        <b>Admin:</b> {o.observacoes_admin}
                      </div>
                    )}
                  </div>
                  {o.status === "AGUARDANDO_APROVACAO" && (
                    <div className="space-y-2 min-w-[240px]">
                      <Textarea
                        data-testid={`obs-${o.id}`}
                        placeholder="Observações (opcional)"
                        value={obs[o.id] || ""}
                        onChange={(e) => setObs({ ...obs, [o.id]: e.target.value })}
                        className="rounded-sm text-xs"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => decidir(o.id, "APROVADA")} className="flex-1 rounded-sm bg-success text-white hover:bg-success/90" data-testid={`aprovar-${o.id}`}>
                          <CheckCircle size={12} className="mr-1" /> Aprovar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decidir(o.id, "REJEITADA")} className="flex-1 rounded-sm" data-testid={`rejeitar-${o.id}`}>
                          <XCircle size={12} className="mr-1" /> Rejeitar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="prestadores" className="mt-6">
          <Card className="p-6 rounded-sm border-border">
            <p className="text-xs text-muted-foreground mb-4">
              Lista de acesso autorizada — utilize esta lista na portaria para liberar terceiros que executam obras nos lojistas.
            </p>
            {prestadores.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm border border-dashed rounded-sm">
                Nenhum prestador cadastrado ainda.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-2">
                {prestadores.map((p) => (
                  <div key={p.id} data-testid={`prestador-${p.id}`} className="border border-border p-3 rounded-sm">
                    <div className="font-semibold">{p.nome}</div>
                    <div className="text-xs font-mono text-muted-foreground">
                      RG {p.rg} {p.cpf && `· CPF ${p.cpf}`}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Lojista: <b>{p.razao_social}</b>
                      {p.funcao && <> · {p.funcao}</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
